import type { APIBodyParams, APIQueryParams, APIResource, PaginatedAPIDecorator, PaginatedAPIResults } from '~~/entities/api'
import { decoratePaginatedAPIResponse } from '~~/lib/utils'

export interface Config {
  retry?: number
  retryDelay?: number
  headers?: Record<string, string>
  signal?: AbortSignal
}

export function useAPIClient<Resource extends APIResource, QueryParams extends APIQueryParams, BodyParams extends APIBodyParams>(
  basePath: string,
) {
  async function listItems(params?: QueryParams, retryConfig?: Config): Promise<Resource[]> {
    return $fetch<Resource[]>(basePath, {
      ...useFetchSharedOptions(),
      params,
      ...retryConfig,
    })
  }

  async function listItemsPaginated(params?: QueryParams, retryConfig?: Config): Promise<PaginatedAPIDecorator<Resource, QueryParams>> {
    return decoratePaginatedAPIResponse(
      await $fetch<PaginatedAPIResults<Resource>>(basePath, {
        ...useFetchSharedOptions(),
        params,
        ...retryConfig,
      }),
    )
  }

  async function getItem(id: string | number, params?: QueryParams, retryConfig?: Config): Promise<Resource> {
    return $fetch<Resource>(`${basePath}/${id}`, {
      ...useFetchSharedOptions(),
      method: 'GET',
      params,
      ...retryConfig,
    })
  }

  async function createItem(body?: BodyParams, retryConfig?: Config): Promise<Resource> {
    return $fetch<Resource>(basePath, {
      ...useFetchSharedOptions(),
      method: 'POST',
      body,
      ...retryConfig,
    })
  }

  async function updateItem(id: string | number, body: BodyParams, retryConfig?: Config): Promise<Resource> {
    return $fetch<Resource>(`${basePath}/${id}`, {
      ...useFetchSharedOptions(),
      method: 'PATCH',
      body,
      ...retryConfig,
    })
  }

  async function deleteItem(id: string | number, retryConfig?: Config): Promise<void> {
    return $fetch<void>(`${basePath}/${id}`, {
      ...useFetchSharedOptions(),
      method: 'DELETE',
      ...retryConfig,
    })
  }

  async function cloneItem(id: string | number, data?: BodyParams, retryConfig?: Config): Promise<Resource> {
    return $fetch<Resource>(`${basePath}/${id}/clone`, {
      ...useFetchSharedOptions(),
      method: 'POST',
      body: {
        data,
      },
      ...retryConfig,
    })
  }

  return {
    getItem,
    listItems,
    listItemsPaginated,
    createItem,
    updateItem,
    deleteItem,
    cloneItem,
  }
}
